/**
 * Customer-Mode empty state for the Executive Dashboard (S05).
 * Shown for ALL role variants when the tenant has no data yet.
 * Drives the first-use experience: connect vendor → capture
 * opportunity → register agent → author policy.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Plug,
  Compass,
  ShieldAlert,
  ClipboardCheck,
  ArrowRight,
  Database,
} from "lucide-react";
import { Flywheel } from "@/components/brand/Flywheel";
import { useTenantData } from "@/contexts/TenantModeContext";

const STEPS = [
  {
    number: "01",
    title: "Connect your first vendor",
    description:
      "OpenAI, Anthropic, AWS Bedrock, Google Vertex AI, Azure OpenAI, IBM watsonx — read-only or active control.",
    icon: Plug,
    path: "/vendors/integrations",
    testId: "onboard-connect-vendor",
  },
  {
    number: "02",
    title: "Capture an opportunity",
    description:
      "Add an AI use-case to the radar. ALGOS will rank it by ROI, effort and risk.",
    icon: Compass,
    path: "/identify/opportunities",
    testId: "onboard-add-opportunity",
  },
  {
    number: "03",
    title: "Register an agent",
    description:
      "Document an existing deployed AI agent. Bring it under governance immediately.",
    icon: ShieldAlert,
    path: "/govern/agents",
    testId: "onboard-add-agent",
  },
  {
    number: "04",
    title: "Author a policy",
    description:
      "Define the rules that govern your AI estate — data residency, vendor allow-list, autonomy caps.",
    icon: ClipboardCheck,
    path: "/govern/policies",
    testId: "onboard-add-policy",
  },
];

export default function CustomerDashboardEmpty({ userName, roleCode }) {
  const navigate = useNavigate();
  const { setMode } = useTenantData();

  return (
    <div
      className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden anim-fade-up"
      data-testid="customer-empty-state"
    >
      <div className="absolute -right-40 -bottom-40 pointer-events-none">
        <Flywheel size={620} opacity={0.04} />
      </div>

      <div className="relative max-w-3xl">
        <p className="eyebrow">Welcome · {roleCode || "Executive"} view</p>
        <h1 className="text-display-lg text-ink-primary mt-1">
          {userName ? `Welcome, ${userName}.` : "Welcome."}
        </h1>
        <p className="text-ink-secondary mt-3 max-w-xl text-[15px]">
          Your tenant is ready. Begin by connecting your first AI vendor or by
          capturing the first AI opportunity. ALGOS will accrue your
          governed estate from there.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
          {STEPS.map((s) => (
            <button
              key={s.number}
              type="button"
              onClick={() => navigate(s.path)}
              data-testid={s.testId}
              className="group surface-card p-5 text-left transition-all hover:border-tralenex-300 hover:shadow-e2"
            >
              <div className="flex items-start justify-between">
                <span className="text-meta font-mono text-ink-disabled tabular-nums">
                  {s.number}
                </span>
                <s.icon
                  size={16}
                  className="text-ink-secondary group-hover:text-tralenex-700 transition-colors"
                />
              </div>
              <p className="mt-3 text-h3 text-ink-primary">{s.title}</p>
              <p className="mt-1 text-meta text-ink-secondary">{s.description}</p>
              <p className="mt-4 text-meta font-semibold text-tralenex-700 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Begin <ArrowRight size={12} />
              </p>
            </button>
          ))}
        </div>

        <div className="mt-10 inline-flex items-center gap-3 px-4 py-3 rounded-md border border-border-subtle bg-raised">
          <Database size={16} className="text-gold-600" />
          <div className="text-meta">
            <span className="text-ink-secondary">
              Want to see ALGOS with a mature dataset?{" "}
            </span>
            <button
              type="button"
              onClick={() => setMode("demo")}
              className="text-tralenex-700 font-semibold hover:text-tralenex-900"
              data-testid="onboard-switch-to-demo"
            >
              Switch to Demo Mode →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
